import { useEffect, useState } from 'react';
import PortalIdParser from 'PortalIdParser';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UILink from 'UIComponents/link/UILink';
import UIModal from 'UIComponents/dialog/UIModal';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import { H2 } from 'UIComponents/elements/headings';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIButton from 'UIComponents/button/UIButton';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import { gate } from 'hub-http/gates';
import TimezoneEditor from '../components/timezone/TimezoneEditor';
import useFetchUserObject from '../hooks/useFetchUserObject';
import { HS_STANDARD_TIME_ZONE, HS_TIMEZONE_AUTO_UPDATE_ENABLED } from '../constants/CrmProperties';
import useFetchUserInfo from '../hooks/useFetchUserInfo';
import { useUpdateUserObjectProperties } from '../hooks/useUpdateUserObjectProperties';
import { useHasGate } from '../hooks/useGates';
import { getDeviceTimezone } from '../utils/timezoneUtils';
import { setCachedUserObjectTimezone } from '../utils/hydrateUserInfo';
import usageTracker from '../utils/usageTracker';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const TimezoneModalView = ({
  onSave,
  onCancel,
  userId,
  isLoading,
  isSaving,
  hasError,
  tz,
  setTZ,
  tzAutoUpdateEnabled,
  setTzAutoUpdateEnabled,
  showTzAutoUpdateCheckbox
}) => {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onCancel
      }), /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ui-asset-management-lib.timezoneModal.title"
        })
      })]
    }), /*#__PURE__*/_jsxs(UIDialogBody, {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "ui-asset-management-lib.timezoneModal.description1"
      }), /*#__PURE__*/_jsx(View, {
        marginBlockStart: "600",
        as: "div",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "ui-asset-management-lib.timezoneModal.description2_jsx",
          elements: {
            UILink
          },
          options: {
            href: `/settings/${PortalIdParser.get()}/users/user/${userId}?tab=preferencesTab`
          }
        })
      }), hasError && /*#__PURE__*/_jsx(UIErrorMessage, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ui-asset-management-lib.timezoneModal.errors.generic"
        })
      }), !hasError && isLoading && /*#__PURE__*/_jsx(UILoadingSpinner, {
        grow: true,
        minHeight: 100
      }), !hasError && !isLoading && /*#__PURE__*/_jsx(TimezoneEditor, {
        tz: tz,
        setTZ: setTZ,
        setTzAutoUpdateEnabled: setTzAutoUpdateEnabled,
        tzAutoUpdateEnabled: tzAutoUpdateEnabled,
        showTzAutoUpdateCheckbox: showTzAutoUpdateCheckbox
      })]
    }), /*#__PURE__*/_jsxs(UIDialogFooter, {
      children: [/*#__PURE__*/_jsx(UILoadingButton, {
        "data-test-id": "save-button",
        use: "primary",
        onClick: onSave,
        loading: isSaving,
        disabled: isLoading || hasError || isSaving,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ui-asset-management-lib.timezoneModal.updateButton"
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        use: "secondary",
        onClick: onCancel,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ui-asset-management-lib.timezoneModal.cancelButton"
        })
      })]
    })]
  });
};
export const TimezoneModalForm = ({
  deviceTz,
  initialTz,
  initialTzAutoUpdateEnabled,
  userId,
  onSave,
  onCancel,
  showTzAutoUpdateCheckbox
}) => {
  const [stagedTz, setStagedTz] = useState(initialTz || deviceTz);
  const [tzAutoUpdateEnabled, setTzAutoUpdateEnabled] = useState(initialTzAutoUpdateEnabled);
  const {
    updateUserObjectProperties,
    result
  } = useUpdateUserObjectProperties();
  useEffect(() => {
    usageTracker.track('userObjectTz', {
      action: 'open',
      tz: initialTz || '',
      autoUpdate: initialTzAutoUpdateEnabled || 'unknown'
    });
  }, [initialTz, initialTzAutoUpdateEnabled]);
  const handleSave = () => {
    updateUserObjectProperties({
      variables: {
        userId,
        properties: {
          [HS_STANDARD_TIME_ZONE]: tzAutoUpdateEnabled ? deviceTz : stagedTz,
          [HS_TIMEZONE_AUTO_UPDATE_ENABLED]: tzAutoUpdateEnabled ? 'true' : 'false'
        },
        isViewingSelf: true
      }
    }).then(() => {
      setCachedUserObjectTimezone(tzAutoUpdateEnabled ? deviceTz : stagedTz);
      usageTracker.track('userObjectTz', {
        action: 'save',
        tz: tzAutoUpdateEnabled ? deviceTz : stagedTz,
        autoUpdate: tzAutoUpdateEnabled ? 'true' : 'false'
      });
    }).catch(console.error).finally(onSave);
  };
  return /*#__PURE__*/_jsx(TimezoneModalView, {
    onSave: handleSave,
    onCancel: () => {
      usageTracker.track('userObjectTz', {
        action: 'cancel',
        tz: tzAutoUpdateEnabled ? deviceTz : stagedTz,
        autoUpdate: tzAutoUpdateEnabled ? 'true' : 'false'
      });
      onCancel();
    },
    userId: userId,
    isSaving: result.loading,
    isLoading: false,
    hasError: !!result.error,
    tz: tzAutoUpdateEnabled ? deviceTz : stagedTz,
    setTZ: setStagedTz,
    tzAutoUpdateEnabled: tzAutoUpdateEnabled,
    setTzAutoUpdateEnabled: setTzAutoUpdateEnabled,
    showTzAutoUpdateCheckbox: !!deviceTz && showTzAutoUpdateCheckbox
  });
};
// Don't allow the user to do anything while loading/error
const dummyProps = {
  onSave: () => {},
  onCancel: () => {},
  userId: 0,
  isLoading: false,
  isSaving: false,
  tz: '',
  setTZ: () => {},
  tzAutoUpdateEnabled: false,
  setTzAutoUpdateEnabled: () => {},
  showTzAutoUpdateCheckbox: false,
  hasError: false
};
const UserObjectTimezoneModal = ({
  onSave,
  onCancel
}) => {
  var _userInfo$data, _I18n$moment$userObje, _I18n$moment, _I18n$moment$userObje2, _userObject$data, _userInfo$data2;
  const hasAutoUpdateGate = useHasGate(gate('UserSetup:TimezoneAutoUpdate'));
  const userInfo = useFetchUserInfo();
  const userObject = useFetchUserObject(((_userInfo$data = userInfo.data) === null || _userInfo$data === void 0 ? void 0 : _userInfo$data.user.user_id) || 0, [HS_TIMEZONE_AUTO_UPDATE_ENABLED]);
  const deviceTz = getDeviceTimezone();
  // We should use the time zone from I18n.moment.userObjectTz()
  // and _not_ fetch the User object `hs_standard_time_zone` from the CRM.
  // This ensures that the time zone we display here is the one used to initialize moment.js.
  const userObjectTz = (_I18n$moment$userObje = (_I18n$moment = I18n.moment).userObjectTz) === null || _I18n$moment$userObje === void 0 || (_I18n$moment$userObje = _I18n$moment$userObje.call(_I18n$moment)) === null || _I18n$moment$userObje === void 0 || (_I18n$moment$userObje2 = _I18n$moment$userObje.tz) === null || _I18n$moment$userObje2 === void 0 ? void 0 : _I18n$moment$userObje2.call(_I18n$moment$userObje);
  const userObjectTZAutoUpdateEnabled = (_userObject$data = userObject.data) === null || _userObject$data === void 0 ? void 0 : _userObject$data.properties[HS_TIMEZONE_AUTO_UPDATE_ENABLED];
  if (!deviceTz || hasAutoUpdateGate.error || userObject.error || userInfo.error) return /*#__PURE__*/_jsx(UIModal, {
    children: /*#__PURE__*/_jsx(TimezoneModalView, Object.assign({}, dummyProps, {
      hasError: true,
      onCancel: onCancel
    }))
  });
  if (hasAutoUpdateGate.loading || userObject.loading || userInfo.loading) {
    return /*#__PURE__*/_jsx(UIModal, {
      children: /*#__PURE__*/_jsx(TimezoneModalView, Object.assign({}, dummyProps, {
        isLoading: true,
        onCancel: onCancel
      }))
    });
  }
  return /*#__PURE__*/_jsx(UIModal, {
    children: /*#__PURE__*/_jsx(TimezoneModalForm, {
      deviceTz: deviceTz,
      initialTz: userObjectTz,
      initialTzAutoUpdateEnabled: !!hasAutoUpdateGate.hasGate && userObjectTZAutoUpdateEnabled === 'true',
      userId: ((_userInfo$data2 = userInfo.data) === null || _userInfo$data2 === void 0 ? void 0 : _userInfo$data2.user.user_id) || 0,
      onSave: () => {
        onSave === null || onSave === void 0 || onSave();
        window.location.reload();
      },
      onCancel: onCancel,
      showTzAutoUpdateCheckbox: hasAutoUpdateGate.hasGate || false
    })
  });
};
export default UserObjectTimezoneModal;